import { useEffect, useState } from 'react';

/**
 * A search box that reports upward once the typing stops, not on every keystroke.
 *
 * Two effects, and the second is the one that is easy to get wrong: a change coming from the
 * parent — a reset, a switch of service desk — has to win, while text still being typed keeps
 * its untrimmed form. Comparing on the trimmed value is what distinguishes the two.
 *
 * @param value - the committed value the parent holds, or undefined for none
 * @param onCommit - called with the trimmed text, or undefined when it empties
 */
export const useDebouncedSearch = (
  value: string | undefined,
  onCommit: (next: string | undefined) => void,
  delay = 300
): [string, (next: string) => void] => {
  const [input, setInput] = useState(value ?? '');

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = input.trim();
      if ((value ?? '') !== next) onCommit(next || undefined);
    }, delay);
    return () => clearTimeout(handle);
  }, [input, value, onCommit, delay]);

  useEffect(() => {
    setInput((current) => (current.trim() === (value ?? '') ? current : value ?? ''));
  }, [value]);

  return [input, setInput];
};
